<template>
  <div>
    <div class="d-flex justify-content-center align-items-center">
      <button
        v-if="items?.length > cardsPerSlide"
        class="carousel-control me-space-md"
        :class="{ disable: isFirstPage }"
        :disabled="isFirstPage"
        @click="showPreviousSlide"
      >
        <img alt="left-chevron" :src="left" />
      </button>

      <transition name="slide-fade" tag="div" mode="out-in">
        <div v-bind:key="currentPage">
          <slot v-bind:paginatedItems="paginatedItems" />
        </div>
      </transition>

      <button
        v-if="items?.length > cardsPerSlide"
        class="carousel-control ms-space-md"
        :class="{ disable: isLastPage }"
        :disabled="isLastPage"
        @click="showNextSlide"
      >
        <img alt="right-chevron" :src="right" />
      </button>
    </div>

    <div v-if="items.length > cardsPerSlide" class="text-center mb-space-sm">
      <button
        class="carousel-indicator mx-space-xxs"
        :class="{ active: currentPage === count }"
        v-for="(count, index) in pageCount"
        :key="index"
        @click="handleIndicatorClick(count)"
        aria-label="carousel indicator"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends unknown">
import { useOffsetPagination } from "@vueuse/core";
import { computed, ref } from "vue";
import left from "~/assets/img/left.svg?inline";
import right from "~/assets/img/right.svg?inline";

interface Props {
  items: T[];
}

const props = withDefaults(defineProps<Props>(), {});

const emits = defineEmits<{
  /**
   *  emitted when next button clicked.
   */
  (e: "nextSlideClick"): void;
  /**
   *  emitted when previous button clicked.
   */
  (e: "previousSlideClick"): void;
  /**
   *  emitted when cta clicked.
   */
  (e: "indicatorClick"): void;
}>();

const cardsPerSlide = ref(1);

const returnToFirstPage = () => {
  currentPage.value = 1;
};

const {
  currentPage,
  currentPageSize,
  pageCount,
  isFirstPage,
  isLastPage,
  prev,
  next,
} = useOffsetPagination({
  total: props.items ? props.items.length : 0,
  page: cardsPerSlide.value,
  pageSize: 1,
  onPageSizeChange: returnToFirstPage,
});

const indexStart = computed(() => {
  return currentPage.value == 1
    ? 0
    : (currentPage.value - 1) * currentPageSize.value;
});

const indexEnd = computed(() => {
  return currentPage.value == 1
    ? currentPageSize.value
    : currentPage.value * currentPageSize.value;
});

const paginatedItems = computed(() => {
  return props.items.slice(indexStart.value, indexEnd.value) as {
    caption: string | null;
    image: string;
    imageAlt: string;
  }[];
});

const showPreviousSlide = () => {
  prev();
  emits("previousSlideClick");
};

const showNextSlide = () => {
  next();
  emits("nextSlideClick");
};

const handleIndicatorClick = (slideNumber: number) => {
  currentPage.value = slideNumber;
  emits("indicatorClick");
};
</script>

<style lang="scss" scoped>
.carousel-control {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 1px solid var(--rds-light-4);
  background: var(--rds-light-3);
  cursor: pointer;
}
.disable {
  opacity: 0.5;
  cursor: auto;
}
.carousel-indicator {
  display: inline-block;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background-color: var(--rds-light-4);
  cursor: pointer;
  border: 0;
  &:focus {
    outline: 1px solid var(--rds-dark-3);
  }
}
.carousel-indicator.active {
  background-color: var(--rds-dark-3);
}
.slide-fade-enter-active {
  transition: opacity 0.2s ease-in, transform 0.1s ease-in;
}
.slide-fade-leave-active {
  transition: opacity 0.4s ease-out, transform 0.3s ease-out;
}
.slide-fade-enter {
  transform: translateX(20px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}
</style>
